import React from 'react';
import { Mic, Square, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { WordStatus } from '../hooks/useShadowingSentenceTracker';

export interface ShadowingMicPopoverProps {
  isListening: boolean;
  hasTtsFinished: boolean;
  isTtsPlaying: boolean;
  transcript: string;
  wordStatuses: WordStatus[];
  isSentenceComplete: boolean;
  ttsRate: number;
  onCycleTtsRate: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onReplayTts: () => void;
  onPrevSentence: () => void;
  onNextSentence: () => void;
}

const ghostBtn = 'group relative w-11 h-11 rounded-full flex items-center justify-center text-[#E6E0D6]/55 hover:text-[#E6E0D6] hover:bg-white/[0.07] transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-90 disabled:opacity-25 disabled:pointer-events-none';

/** Controles do Shadowing (Design 2.0): uma fileira só, microfone no centro com ondas ao escutar. */
export const ShadowingMicPopover: React.FC<ShadowingMicPopoverProps> = ({
  isListening,
  isTtsPlaying,
  transcript,
  wordStatuses,
  isSentenceComplete,
  ttsRate,
  onCycleTtsRate,
  onStartRecording,
  onStopRecording,
  onReplayTts,
  onPrevSentence,
  onNextSentence
}) => {
  const { t } = useTranslation();
  const correctCount = wordStatuses.filter(w => w.status === 'correct').length;
  const totalCount = wordStatuses.length;
  const score = totalCount > 0 ? correctCount / totalCount : 0;

  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center gap-5 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* O que o microfone ouviu */}
      <p className={`min-h-[20px] max-w-full truncate text-center text-[13px] italic text-[#E6E0D6]/45 transition-opacity duration-300 ${transcript ? 'opacity-100' : 'opacity-0'}`}>
        {transcript ? `“${transcript}”` : ' '}
      </p>

      <div className="w-full flex items-center justify-between">
        <button type="button" onClick={onPrevSentence} className={ghostBtn} aria-label={t('shadowing.prev', 'Frase anterior')} title={t('shadowing.prev', 'Frase anterior')}>
          <ChevronLeft size={20} strokeWidth={1.75} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
        </button>

        <button
          type="button"
          onClick={onReplayTts}
          disabled={isTtsPlaying || isListening}
          className={ghostBtn}
          aria-label={t('shadowing.replay', 'Ouvir de novo')}
          title={t('shadowing.replay', 'Ouvir de novo')}
        >
          <RotateCcw size={17} strokeWidth={1.75} className={isTtsPlaying ? 'animate-spin text-ok' : 'transition-transform duration-500 group-hover:-rotate-180'} />
        </button>

        {/* Microfone */}
        <div className="relative">
          {isListening && (
            <>
              <span className="absolute inset-0 rounded-full bg-bad/30 animate-ping" />
              <span className="absolute -inset-3 rounded-full border border-bad/25 animate-pulse" />
            </>
          )}
          {!isListening ? (
            <button
              type="button"
              onClick={onStartRecording}
              disabled={isSentenceComplete}
              aria-label={t('shadowing.record', 'Falar')}
              className="group relative w-[68px] h-[68px] rounded-full bg-[#E6E0D6] text-[#1C2430] flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-105 active:scale-95 disabled:opacity-30"
            >
              <Mic size={24} strokeWidth={1.75} className="transition-transform duration-300 group-hover:scale-110" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onStopRecording}
              aria-label={t('shadowing.stop', 'Parar')}
              className="relative w-[68px] h-[68px] rounded-full bg-bad text-white flex items-center justify-center transition-all duration-300 active:scale-95"
            >
              <Square size={18} fill="currentColor" strokeWidth={0} />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onCycleTtsRate}
          className={`${ghostBtn} font-mono text-[11px] tabular-nums`}
          aria-label={t('shadowing.speed', 'Velocidade')}
          title={t('shadowing.speed', 'Velocidade')}
        >
          <span key={ttsRate} className="animate-in fade-in zoom-in-90 duration-300">{ttsRate}×</span>
        </button>

        <button type="button" onClick={onNextSentence} className={ghostBtn} aria-label={t('shadowing.next', 'Próxima frase')} title={t('shadowing.next', 'Próxima frase')}>
          <ChevronRight size={20} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </button>
      </div>

      {/* Acertos: barra fina + contador */}
      <div className="w-40 flex items-center gap-3">
        <div className="flex-1 h-[3px] rounded-full bg-white/[0.08] overflow-hidden">
          <div className="h-full rounded-full bg-ok transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]" style={{ width: `${score * 100}%` }} />
        </div>
        <span className="font-mono text-[11px] tabular-nums text-[#E6E0D6]/50 whitespace-nowrap">{correctCount}/{totalCount}</span>
      </div>
    </div>
  );
};

export default ShadowingMicPopover;
